import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, ArrowUpRight, Buildings, Envelope, Globe, MapPin, Phone,
  Scales, ShieldCheck, Sparkle, Warning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useI18n } from "@/context/I18nContext";

const unavailable = (value, locale) => value || (locale === "en" ? "Not available" : "Mevcut değil");

const roleLabel = (type, locale) => {
  const labels = {
    reseller_sales_point: ["Satış noktası / bayi", "Reseller / sales point"],
    sponsor_prospect: ["Sponsor adayı", "Sponsor prospect"],
    event_participant_prospect: ["Etkinlik katılımcısı adayı", "Event participant prospect"],
    personal_b2c_service_provider: ["Kişisel hizmet sağlayıcısı", "Personal service provider"],
    audience_access_channel: ["Hedef kitle erişim kanalı", "Target-audience access channel"],
    b2b_sales_prospect: ["B2B satış adayı", "B2B sales prospect"],
  };
  return labels[type]?.[locale === "en" ? 1 : 0] || unavailable(null, locale);
};

const evidenceLabel = (level, locale) => ({
  strong: locale === "en" ? "Strong evidence" : "Güçlü kanıt",
  partial: locale === "en" ? "Partial evidence" : "Kısmi kanıt",
}[level] || unavailable(null, locale));

const evidenceRank = (lead) => lead.qualification?.evidence_level === "strong" ? 2 : lead.qualification?.evidence_level === "partial" ? 1 : 0;

const getWinner = (leads) => {
  const ranked = leads
    .filter((lead) => Number.isFinite(Number(lead.match_score)))
    .map((lead) => ({ lead, score: Number(lead.match_score), evidence: evidenceRank(lead) }))
    .sort((a, b) => b.score - a.score || b.evidence - a.evidence);
  if (ranked.length < 2) return null;
  const [top, next] = ranked;
  if (top.score - next.score < 8 || top.evidence < next.evidence) return null;
  return { top, next };
};

export default function CompanyComparison() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { locale } = useI18n();
  const t = (tr, en) => (locale === "en" ? en : tr);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  const requestedIds = useMemo(() => (params.get("ids") || "").split(",").filter(Boolean).slice(0, 4), [params]);
  const leads = useMemo(() => {
    const byId = new Map((session?.leads || []).map((lead) => [lead.id, lead]));
    return requestedIds.map((leadId) => byId.get(leadId)).filter(Boolean);
  }, [session, requestedIds]);
  const winner = useMemo(() => getWinner(leads), [leads]);

  useEffect(() => {
    let active = true;
    api.get(`/history/sessions/${id}`)
      .then(({ data }) => { if (active) setSession(data); })
      .catch(() => { if (active) toast.error(locale === "en" ? "Could not load session" : "Oturum yüklenemedi"); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [id, locale]);

  const openLead = (leadId) => nav(`/musteriler/${leadId}?session_id=${id}`);

  return (
    <div className="p-6 md:p-10 space-y-6 max-w-[1500px]" data-testid="company-comparison-page">
      <div className="flex items-start gap-3">
        <Button variant="ghost" size="icon" onClick={() => nav(`/gecmis/${id}`)} data-testid="comparison-back-to-session">
          <ArrowLeft size={16} />
        </Button>
        <div className="min-w-0">
          <div className="acas-eyebrow mb-1 flex items-center gap-1.5"><Scales size={12} weight="duotone" />{t("Firma karşılaştırma", "Company comparison")}</div>
          <h1 data-testid="comparison-title" className="text-2xl sm:text-3xl font-extrabold tracking-tight">{t("Hangi firma daha uygun?", "Which company is the better fit?")}</h1>
          <p data-testid="comparison-goal" className="text-sm text-muted-foreground mt-2">{session?.raw_goal || t("Arama hedefi mevcut değil.", "Search goal is not available.")}</p>
        </div>
      </div>

      {loading && <Skeleton className="h-80 w-full" data-testid="comparison-loading" />}

      {!loading && leads.length < 2 && (
        <div className="rounded-lg border border-border/60 bg-card p-8 text-sm text-muted-foreground" data-testid="comparison-insufficient-selection">
          {t("Karşılaştırma için bu oturumdan en az iki görünür firma seçin.", "Select at least two visible companies from this session to compare.")}
        </div>
      )}

      {!loading && leads.length >= 2 && (
        <>
          <div className={`rounded-lg border p-4 flex items-start gap-3 ${winner ? "border-primary/35 bg-primary/[0.03]" : "border-border/60 bg-card"}`} data-testid="comparison-recommendation">
            {winner ? <Sparkle size={16} weight="fill" className="text-primary mt-0.5" /> : <Scales size={16} weight="duotone" className="text-muted-foreground mt-0.5" />}
            <div className="text-sm leading-relaxed">
              {winner
                ? t(
                  `${winner.top.lead.company_name}, ${winner.next.lead.company_name} firmasından daha yüksek uyum skoruna sahip (${winner.top.score} / ${winner.next.score}). Mevcut kanıt seviyesi de daha düşük değil.`,
                  `${winner.top.lead.company_name} has a higher fit score than ${winner.next.lead.company_name} (${winner.top.score} / ${winner.next.score}) without a lower evidence level.`
                )
                : t("Mevcut skor ve kanıtlar net bir üstün aday göstermiyor. Farkları birlikte değerlendirin.", "The available scores and evidence do not identify a clear stronger candidate. Review the differences side by side.")}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="comparison-grid">
            {leads.map((lead) => {
              const qualification = lead.qualification || {};
              const isWinner = winner?.top.lead.id === lead.id;
              const facts = qualification.verified_facts || [];
              const unknowns = qualification.unknowns || [];
              return (
                <article key={lead.id} data-testid={`comparison-card-${lead.id}`} className={`rounded-lg border bg-card p-5 space-y-5 min-w-0 ${isWinner ? "border-primary/50 ring-1 ring-primary/20" : "border-border/60"}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h2 data-testid={`comparison-company-name-${lead.id}`} className="font-bold text-base truncate">{lead.company_name || unavailable(null, locale)}</h2>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        <Badge data-testid={`comparison-industry-${lead.id}`} variant="outline" className="font-normal text-[10px]"><Buildings size={11} className="mr-1" />{unavailable(lead.industry, locale)}</Badge>
                        <Badge data-testid={`comparison-location-${lead.id}`} variant="outline" className="font-normal text-[10px]"><MapPin size={11} className="mr-1" />{unavailable(lead.location, locale)}</Badge>
                      </div>
                    </div>
                    {isWinner && <Badge data-testid={`comparison-winner-${lead.id}`} className="text-[10px] shrink-0">{t("Daha güçlü uyum", "Stronger fit")}</Badge>}
                  </div>

                  <ComparisonRow testid={`comparison-score-${lead.id}`} label={t("Uyum skoru", "Fit score")} value={Number.isFinite(Number(lead.match_score)) ? `${lead.match_score}/100` : unavailable(null, locale)} strong />
                  <ComparisonRow testid={`comparison-role-${lead.id}`} label={t("Ticari rol", "Commercial role")} value={roleLabel(qualification.relationship_type, locale)} />
                  <ComparisonRow testid={`comparison-suitability-${lead.id}`} label={t("Ticari uygunluk", "Commercial suitability")} value={unavailable(qualification.why_suitable, locale)} />
                  <ComparisonRow testid={`comparison-evidence-${lead.id}`} label={t("Kanıt", "Evidence")} value={evidenceLabel(qualification.evidence_level, locale)} />
                  <ComparisonRow testid={`comparison-relevance-${lead.id}`} label={t("Arama ile ilişkisi", "Search relevance")} value={unavailable(lead.reason, locale)} />

                  <div>
                    <div className="acas-eyebrow mb-2 flex items-center gap-1"><ShieldCheck size={11} />{t("Güçlü taraflar", "Strengths")}</div>
                    {facts.length ? <ul className="space-y-1.5 text-xs" data-testid={`comparison-facts-${lead.id}`}>{facts.slice(0, 4).map((fact, index) => <li key={`${fact.kind}-${index}`}>• {fact.value}</li>)}</ul> : <p className="text-xs text-muted-foreground">{t("Doğrulanmış bilgi mevcut değil.", "No verified information available.")}</p>}
                  </div>

                  <div>
                    <div className="acas-eyebrow mb-2 flex items-center gap-1"><Warning size={11} />{t("Eksik bilgiler", "Missing information")}</div>
                    {unknowns.length ? <ul className="space-y-1.5 text-xs text-muted-foreground" data-testid={`comparison-unknowns-${lead.id}`}>{unknowns.slice(0, 4).map((item, index) => <li key={index}>• {item}</li>)}</ul> : <p className="text-xs text-muted-foreground">{t("Eksik bilgi kaydı yok.", "No missing-information record.")}</p>}
                  </div>

                  <div className="border-t border-border/60 pt-4 space-y-2 text-xs">
                    <div className="acas-eyebrow">{t("İletişim", "Contact")}</div>
                    {lead.website && <a href={lead.website} target="_blank" rel="noreferrer noopener" data-testid={`comparison-website-${lead.id}`} className="flex items-center gap-1.5 text-primary hover:underline break-all"><Globe size={12} />{lead.website}</a>}
                    {lead.contact_email && <div data-testid={`comparison-email-${lead.id}`} className="flex items-center gap-1.5 break-all"><Envelope size={12} />{lead.contact_email}</div>}
                    {lead.contact_phone && <div data-testid={`comparison-phone-${lead.id}`} className="flex items-center gap-1.5"><Phone size={12} />{lead.contact_phone}</div>}
                    {!lead.website && !lead.contact_email && !lead.contact_phone && <p data-testid={`comparison-contact-empty-${lead.id}`} className="text-muted-foreground">{unavailable(null, locale)}</p>}
                  </div>

                  <Button onClick={() => openLead(lead.id)} data-testid={`comparison-open-lead-${lead.id}`} className="w-full gap-1.5">
                    {t("Firma detayını aç", "Open company details")} <ArrowUpRight size={14} />
                  </Button>
                </article>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function ComparisonRow({ label, value, strong = false, testid }) {
  return (
    <div data-testid={testid} className="border-y border-border/50 py-3">
      <div className="acas-eyebrow mb-1">{label}</div>
      <div className={strong ? "text-xl font-bold text-primary" : "text-sm leading-relaxed"}>{value}</div>
    </div>
  );
}